import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ButtonSpinner } from '../ui/Loader';
import {
  useGetBotButtonLabelsQuery,
  useUpdateBotButtonLabelsMutation,
  useTranslateBotButtonLabelMutation,
  BotButtonLang,
  BotButtonConfigResponse,
  BotButtonLabelEntry,
} from '../../store/api/adminApi';

const LANG_META: { code: BotButtonLang; label: string }[] = [
  { code: 'ar', label: 'العربية' },
  { code: 'en', label: 'English' },
  { code: 'yo', label: 'Yorùbá' },
  { code: 'ha', label: 'Hausa' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'es', label: 'Español' },
  { code: 'bn', label: 'বাংলা' },
];

function emptyLangs(): Record<BotButtonLang, string> {
  return { ar: '', en: '', yo: '', ha: '', hi: '', es: '', bn: '' };
}

const STYLE_UI: Record<string, { label: string; className: string }> = {
  '': { label: 'بدون', className: 'bg-neutral-dark/40 border-neutral-medium/40 text-neutral-medium' },
  primary: { label: 'أزرق', className: 'bg-blue-500/20 border-blue-400/50 text-blue-300' },
  success: { label: 'أخضر', className: 'bg-green-500/20 border-green-400/50 text-green-300' },
  danger: { label: 'أحمر', className: 'bg-red-500/20 border-red-400/50 text-red-300' },
};

type Draft = {
  labels: Record<string, Record<BotButtonLang, string>>;
  styles: Record<string, string>;
  rows: string[][];
};

/**
 * Advanced Telegram main-menu button studio: colors, names, row layout.
 */
export function BotButtonStudio() {
  const { data, isLoading } = useGetBotButtonLabelsQuery();
  const [saveConfig, { isLoading: saving }] = useUpdateBotButtonLabelsMutation();
  const [translateLabel, { isLoading: translating }] = useTranslateBotButtonLabelMutation();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [translateSource, setTranslateSource] = useState<BotButtonLang>('ar');
  const [dirty, setDirty] = useState(false);

  const cfg = data?.data as BotButtonConfigResponse | BotButtonLabelEntry[] | undefined;
  const isNewShape = cfg && !Array.isArray(cfg) && Array.isArray((cfg as BotButtonConfigResponse).buttons);

  const buttons: BotButtonLabelEntry[] = useMemo(() => {
    if (!cfg) return [];
    if (Array.isArray(cfg)) return cfg;
    return (cfg as BotButtonConfigResponse).buttons || [];
  }, [cfg]);

  const styleOptions = useMemo(() => {
    if (isNewShape) return (cfg as BotButtonConfigResponse).styles || [];
    return [
      { id: '', labelAr: 'بدون لون' },
      { id: 'primary', labelAr: 'أزرق' },
      { id: 'success', labelAr: 'أخضر' },
      { id: 'danger', labelAr: 'أحمر' },
    ];
  }, [cfg, isNewShape]);

  const byKey = useMemo(() => {
    const m = new Map<string, BotButtonLabelEntry>();
    buttons.forEach((b) => m.set(b.key, b));
    return m;
  }, [buttons]);

  useEffect(() => {
    if (!cfg) return;
    if (Array.isArray(cfg)) {
      const labels: Draft['labels'] = {};
      const styles: Draft['styles'] = {};
      cfg.forEach((b) => {
        labels[b.key] = { ...emptyLangs(), ...b.labels };
        styles[b.key] = b.style || '';
      });
      setDraft({
        labels,
        styles,
        rows: [
          ['button_numbers', 'button_buy_proxy'],
          ['button_my_balance', 'button_master_balance'],
          ['button_my_numbers', 'button_my_proxies'],
          ['button_support', 'button_channel', 'button_language'],
          ['button_admin_dashboard'],
        ],
      });
      setDirty(false);
      return;
    }
    const c = cfg as BotButtonConfigResponse;
    const labels: Draft['labels'] = {};
    const styles: Draft['styles'] = {};
    (c.buttons || []).forEach((b) => {
      labels[b.key] = { ...emptyLangs(), ...b.labels };
      styles[b.key] = b.style || '';
    });
    setDraft({
      labels,
      styles,
      rows: (c.rows || []).map((r) => [...r]),
    });
    setDirty(false);
  }, [cfg]);

  if (isLoading || !draft) {
    return (
      <div className="card-glass rounded-xl p-8 flex justify-center mb-8">
        <ButtonSpinner />
      </div>
    );
  }

  const mainKeysInRows = new Set(draft.rows.flat());
  const submenuButtons = buttons.filter((b) => b.mainMenu === false);
  const orphanMain = buttons.filter((b) => b.mainMenu !== false && !mainKeysInRows.has(b.key));

  const setStyle = (key: string, style: string) => {
    setDraft((prev) =>
      prev ? { ...prev, styles: { ...prev.styles, [key]: style } } : prev
    );
    setDirty(true);
  };

  const setLabel = (key: string, lang: BotButtonLang, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        labels: {
          ...prev.labels,
          [key]: {
            ...emptyLangs(),
            ...prev.labels[key],
            [lang]: value,
          },
        },
      };
    });
    setDirty(true);
  };

  const moveRow = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= draft.rows.length) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const rows = [...prev.rows];
      const tmp = rows[index];
      rows[index] = rows[next];
      rows[next] = tmp;
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const moveInRow = (rowIndex: number, btnIndex: number, dir: -1 | 1) => {
    const next = btnIndex + dir;
    const row = draft.rows[rowIndex];
    if (!row || next < 0 || next >= row.length) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => [...r]);
      const r = rows[rowIndex];
      const tmp = r[btnIndex];
      r[btnIndex] = r[next];
      r[next] = tmp;
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const moveToOtherRow = (fromRow: number, btnIndex: number, toRow: number) => {
    if (toRow < 0 || toRow >= draft.rows.length || fromRow === toRow) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => [...r]);
      const [key] = rows[fromRow].splice(btnIndex, 1);
      if (!key) return prev;
      rows[toRow].push(key);
      return { ...prev, rows: rows.filter((r) => r.length > 0) };
    });
    setDirty(true);
  };

  const splitButtonToNewRow = (rowIndex: number, btnIndex: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => [...r]);
      const [key] = rows[rowIndex].splice(btnIndex, 1);
      if (!key) return prev;
      rows.splice(rowIndex + 1, 0, [key]);
      return { ...prev, rows: rows.filter((r) => r.length > 0) };
    });
    setDirty(true);
  };

  const addOrphanToLastRow = (key: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => [...r]);
      if (!rows.length) rows.push([key]);
      else rows[rows.length - 1].push(key);
      return { ...prev, rows };
    });
    setDirty(true);
  };

  const handleAutoTranslate = async (key: string) => {
    const text = (draft.labels[key]?.[translateSource] || '').trim();
    if (!text) {
      toast.error(`اكتب الاسم أولاً بلغة ${LANG_META.find((l) => l.code === translateSource)?.label}`);
      return;
    }
    try {
      const res = await translateLabel({ text, sourceLang: translateSource }).unwrap();
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              labels: {
                ...prev.labels,
                [key]: { ...emptyLangs(), ...prev.labels[key], ...res.data.labels },
              },
            }
          : prev
      );
      setDirty(true);
      toast.success('تمت الترجمة');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشلت الترجمة');
    }
  };

  const handleSave = async () => {
    try {
      await saveConfig({
        labels: draft.labels,
        styles: draft.styles,
        rows: draft.rows,
      }).unwrap();
      setDirty(false);
      toast.success('تم حفظ أزرار البوت (أسماء + ألوان + ترتيب)');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  const handleResetLayout = () => {
    const defaults =
      isNewShape && (cfg as BotButtonConfigResponse).defaults?.rows
        ? (cfg as BotButtonConfigResponse).defaults!.rows
        : [
            ['button_numbers', 'button_buy_proxy'],
            ['button_my_balance', 'button_master_balance'],
            ['button_my_numbers', 'button_my_proxies'],
            ['button_support', 'button_channel', 'button_language'],
            ['button_admin_dashboard'],
          ];
    setDraft((prev) => (prev ? { ...prev, rows: defaults.map((r) => [...r]) } : prev));
    setDirty(true);
  };

  const renderColorPicker = (key: string, featured?: boolean) => {
    const options = featured
      ? styleOptions.filter((s) => s.id === 'primary' || s.id === 'success' || s.id === 'danger')
      : styleOptions.length
        ? styleOptions
        : [
            { id: '', labelAr: 'بدون' },
            { id: 'primary', labelAr: 'أزرق' },
            { id: 'success', labelAr: 'أخضر' },
            { id: 'danger', labelAr: 'أحمر' },
          ];
    return (
      <div className="flex flex-wrap gap-2">
        {options.map((s) => {
          const id = s.id || '';
          const ui = STYLE_UI[id] || STYLE_UI[''];
          const active = (draft.styles[key] || '') === id;
          return (
            <button
              key={`${key}-${id || 'none'}`}
              type="button"
              onClick={() => setStyle(key, id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-heading border transition-all ${ui.className} ${
                active ? 'ring-2 ring-brand-accent scale-[1.03]' : 'opacity-80 hover:opacity-100'
              }`}
            >
              {s.labelAr || ui.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderNameEditor = (key: string) => (
    <div className="space-y-3 mt-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {LANG_META.map((l) => (
          <div key={l.code}>
            <label className="block text-xs text-neutral-medium mb-1">{l.label}</label>
            <input
              value={draft.labels[key]?.[l.code] || ''}
              onChange={(e) => setLabel(key, l.code, e.target.value)}
              placeholder={byKey.get(key)?.labelAr || key}
              className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-3 py-2 text-sm"
              dir={l.code === 'ar' || l.code === 'ha' ? 'rtl' : 'ltr'}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => handleAutoTranslate(key)}
        disabled={translating}
        className="px-4 py-2 rounded-lg border border-brand-accent/40 text-brand-accent text-sm hover:bg-brand-accent/10 disabled:opacity-50"
      >
        {translating ? 'جاري الترجمة...' : 'ترجمة تلقائية لباقي اللغات'}
      </button>
    </div>
  );

  return (
    <div className="card-glass rounded-xl p-6 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-heading text-brand-accent mb-1">أزرار البوت — استوديو متقدم</h2>
          <p className="text-sm text-neutral-medium leading-relaxed max-w-2xl">
            شراء رقم وشراء بروكسي: اختر لون الخلفية (أزرق / أخضر / أحمر). باقي الأزرار: سمِّها، اختَر لون أو اتركه بدون، ورتّب الصفوف والأزرار كما تريد أن تظهر بالقائمة الرئيسية.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleResetLayout}
            className="px-3 py-2 rounded-lg text-sm border border-neutral-dark/40 text-neutral-medium hover:text-neutral-light"
          >
            إعادة الترتيب الافتراضي
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn-primary px-5 py-2 rounded-lg font-body text-sm disabled:opacity-50"
          >
            {saving ? 'جاري الحفظ…' : 'حفظ الكل'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-neutral-medium">ترجمة من:</span>
        <select
          value={translateSource}
          onChange={(e) => setTranslateSource(e.target.value as BotButtonLang)}
          className="bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-3 py-1.5 text-sm"
        >
          {LANG_META.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
        {dirty && <span className="text-xs text-amber-400">تغييرات غير محفوظة</span>}
      </div>

      {/* Featured purchase buttons */}
      <div className="mb-6">
        <h3 className="text-sm font-heading text-brand-accent mb-3">أزرار الشراء (لون إلزامي)</h3>
        <div className="space-y-3">
          {['button_numbers', 'button_buy_proxy'].map((key) => {
            const meta = byKey.get(key);
            if (!meta) return null;
            const open = openKey === key;
            return (
              <div key={key} className="rounded-xl border border-brand-accent/30 bg-brand-accent/5 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div>
                    <div className="font-heading text-neutral-light">{meta.labelAr}</div>
                    <div className="text-xs text-neutral-medium">{meta.labelEn}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenKey(open ? null : key)}
                    className="text-xs text-brand-accent"
                  >
                    {open ? 'إخفاء الأسماء ▲' : 'تعديل الاسم ▼'}
                  </button>
                </div>
                {renderColorPicker(key, true)}
                {open && renderNameEditor(key)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main menu layout */}
      <div className="mb-6">
        <h3 className="text-sm font-heading text-brand-accent mb-2">ترتيب القائمة الرئيسية (صفوف)</h3>
        <p className="text-xs text-neutral-medium mb-3">
          كل صندوق = صف في التليجرام. حرّك الصف ▲▼، وداخل الصف ◀▶، أو انقل لصف آخر / صف جديد.
        </p>
        <div className="space-y-4">
          {draft.rows.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="rounded-xl border border-neutral-dark/30 bg-brand-primary/10 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-heading text-neutral-medium">صف {rowIndex + 1}</span>
                <div className="flex gap-1">
                  <button type="button" className="w-7 h-7 rounded border border-neutral-dark/40 text-xs disabled:opacity-30" disabled={rowIndex === 0} onClick={() => moveRow(rowIndex, -1)}>▲</button>
                  <button type="button" className="w-7 h-7 rounded border border-neutral-dark/40 text-xs disabled:opacity-30" disabled={rowIndex === draft.rows.length - 1} onClick={() => moveRow(rowIndex, 1)}>▼</button>
                </div>
              </div>
              <div className="space-y-2">
                {row.map((key, btnIndex) => {
                  const meta = byKey.get(key);
                  const open = openKey === key;
                  const styleId = draft.styles[key] || '';
                  return (
                    <div key={`${rowIndex}-${key}`} className="rounded-lg border border-neutral-dark/25 bg-brand-primary/20 p-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex gap-1 shrink-0">
                          <button type="button" className="w-7 h-7 rounded border border-neutral-dark/40 text-xs disabled:opacity-30" disabled={btnIndex === 0} onClick={() => moveInRow(rowIndex, btnIndex, -1)}>◀</button>
                          <button type="button" className="w-7 h-7 rounded border border-neutral-dark/40 text-xs disabled:opacity-30" disabled={btnIndex === row.length - 1} onClick={() => moveInRow(rowIndex, btnIndex, 1)}>▶</button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-sm text-neutral-light truncate">{meta?.labelAr || key}</div>
                          <div className="text-[10px] text-neutral-medium">{meta?.labelEn}</div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <select
                            className="bg-brand-primary/30 border border-neutral-dark/40 rounded px-2 py-1 text-[11px]"
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'new') splitButtonToNewRow(rowIndex, btnIndex);
                              else if (v.startsWith('row:')) moveToOtherRow(rowIndex, btnIndex, Number(v.slice(4)));
                            }}
                          >
                            <option value="">نقل…</option>
                            <option value="new">→ صف جديد</option>
                            {draft.rows.map((_, i) =>
                              i === rowIndex ? null : (
                                <option key={i} value={`row:${i}`}>→ صف {i + 1}</option>
                              )
                            )}
                          </select>
                          <button type="button" className="text-[11px] text-brand-accent px-2" onClick={() => setOpenKey(open ? null : key)}>
                            {open ? '▲' : 'اسم'}
                          </button>
                        </div>
                      </div>
                      <div className="mt-2">
                        {key === 'button_numbers' || key === 'button_buy_proxy'
                          ? renderColorPicker(key, true)
                          : renderColorPicker(key, false)}
                      </div>
                      {styleId && (
                        <div className={`mt-2 inline-flex text-[10px] px-2 py-0.5 rounded-full border ${STYLE_UI[styleId]?.className || ''}`}>
                          لون: {STYLE_UI[styleId]?.label || styleId}
                        </div>
                      )}
                      {open && renderNameEditor(key)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {orphanMain.length > 0 && (
          <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
            <p className="text-xs text-amber-300 mb-2">أزرار خارج الترتيب — أضفها لآخر صف:</p>
            <div className="flex flex-wrap gap-2">
              {orphanMain.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => addOrphanToLastRow(b.key)}
                  className="px-3 py-1.5 rounded-lg text-xs border border-amber-500/40 text-amber-200"
                >
                  + {b.labelAr}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submenu buttons */}
      {submenuButtons.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-heading text-brand-accent mb-2">قائمة الأرقام (فرعية)</h3>
          <p className="text-xs text-neutral-medium mb-3">تظهر بعد ضغط «شراء رقم» — تسمية + لون اختياري.</p>
          <div className="space-y-3">
            {submenuButtons.map((b) => {
              const open = openKey === b.key;
              return (
                <div key={b.key} className="rounded-xl border border-neutral-dark/30 bg-brand-primary/10 p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                      <div className="font-heading text-sm text-neutral-light">{b.labelAr}</div>
                      <div className="text-[10px] text-neutral-medium">{b.labelEn}</div>
                    </div>
                    <button type="button" className="text-xs text-brand-accent" onClick={() => setOpenKey(open ? null : b.key)}>
                      {open ? '▲' : 'اسم'}
                    </button>
                  </div>
                  {renderColorPicker(b.key, false)}
                  {open && renderNameEditor(b.key)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="btn-primary px-6 py-2.5 rounded-lg font-body disabled:opacity-50"
        >
          {saving ? 'جاري الحفظ…' : 'حفظ الكل'}
        </button>
      </div>
    </div>
  );
}

export default BotButtonStudio;
